import {
  HomeDto,
  Resident,
  ResidentFundingSourceDTO,
  ShiftType,
  User,
  VisitType,
} from '@lmc/ts-common'
import { DefaultOptionType } from 'antd/lib/select'
import { RotaPatternModalType } from './RotaPattern/utils'
import { getDateTimeInZone, TIME_FIRST_DATE_FORMAT } from './utils.datetime'
import { PublicationType } from './hooks/useRevisionsPublish'
import { CountChangesResponse } from './hooks/useCountChanges'
import { VISIT } from './constants'
import { RotaInstanceStatus, START_OVERLAY_TYPE } from './types'
import { RuleObject } from 'antd/es/form'

export const getRequiredRule = (fieldName: string) => {
  return {
    required: true,
    message: `The ${fieldName} field is required`,
  }
}

export const getCannotBeEmptyRule = () => {
  return {
    required: true,
    message: `Field cannot be empty.`,
  }
}

export const getMinMaxRule = (min: number, max: number) => ({
  validator: async (_: RuleObject, value: number) => {
    if (value === undefined || value === null) {
      return Promise.resolve()
    }

    if (value < min || value > max) {
      return Promise.reject(
        new Error(`Value must be between ${min} and ${max}.`)
      )
    }

    return Promise.resolve()
  },
})

export const getIsDifferentRule = (
  currentFormValues: any[],
  fieldName: number,
  keyName: string = 'name'
) => {
  return {
    validator: async (_: RuleObject, value: string) => {
      if (!value) return Promise.resolve()

      const matchingIndices = currentFormValues
        .map((formValue: any, index: number) =>
          formValue?.[keyName]?.toLowerCase() === value.toLowerCase()
            ? index
            : -1
        )
        .filter((index: number) => index !== -1)

      const currentIndex = fieldName
      const isDuplicate = matchingIndices.indexOf(currentIndex) > 0

      if (isDuplicate) {
        return Promise.reject(
          new Error(
            `This ${keyName} already exists. Please enter a unique ${keyName}.`
          )
        )
      }

      return Promise.resolve()
    },
  }
}

export const getTeamMembersClockInsRule = (status: RotaInstanceStatus,clockIns: any[] | undefined) => ({
  validator: async (_: RuleObject, value: number) => {
    const clockInsLength = clockIns?.length ?? 0
    const isInProgress = status === RotaInstanceStatus.IN_PROGRESS || clockInsLength > 0

    if (isInProgress && clockInsLength > value) {
      return Promise.reject(
        new Error(
          `Number cannot be lower than clocked in team members`
        )
      )
    }
    return Promise.resolve()
  },
})

export const labelStyle = { width: 128 }

export const getIsCarePackagesEmpty = (resident?: Resident) =>
  !resident?.carePackage?.additionalNotes &&
  !resident?.carePackage?.visitNotes &&
  !resident?.carePackage?.fundingSources?.length

export const getIsContractPreferencesEmpty = (user?: User) =>
  !user?.workProfile?.contractNotes &&
  !user?.workProfile?.contractType &&
  user?.workProfile?.canDoOvertime === undefined &&
  user?.workProfile?.contractedHoursPerWeek === undefined &&
  !user?.workProfile?.availableTransportation?.length

// we can't change options every time we select something
// the mapping between label and value will break
// we have to disabled already used options
export const getFilteredFundingTypeOptions = (
  options: DefaultOptionType[],
  pickedOptions: { type: string }[]
) => {
  const isOptionUsed = (option: DefaultOptionType) => {
    if (option.value !== 'Other') {
      const typesArray = pickedOptions?.map((item) => item?.type)
      const value = option?.value

      if (value) {
        const isUsed = typesArray?.includes(value.toString())

        return isUsed
      }

      return false
    }

    return false
  }

  return options.map((option) => ({
    ...option,
    disabled: isOptionUsed(option),
  }))
}

export const getFilteredFundingTypePatternOptions = (
  options: DefaultOptionType[],
  pickedOptions: { skillId: string }[]
) => {
  const isOptionUsed = (option: DefaultOptionType) => {
    const typesArray = pickedOptions?.map((item) => item?.skillId)
    const value = option?.value

    if (value) {
      const isUsed = typesArray?.includes(value.toString())

      return isUsed
    }

    return false
  }

  return options.map((option) => ({
    ...option,
    disabled: isOptionUsed(option),
  }))
}

export const getFundingSourceName = (
  source: ResidentFundingSourceDTO,
  home: HomeDto
) => {
  const rotaSetting = home.rotaSettings?.fundingTypes.find(
    (fundingType) => fundingType.id === source.type
  )

  return rotaSetting?.name
}

export const getAbbreviation = (str: string) => {
  const words = str.split(' ')
  return words[0].charAt(0).toUpperCase() + words[1].charAt(0).toUpperCase()
}

export const getRotaTypeLabel = (
  type: RotaPatternModalType,
  id: string,
  visitTypes: VisitType[],
  shiftTypes: ShiftType[]
) => {
  if (type === VISIT) {
    return visitTypes?.find((item) => item?.id === id)?.name
  }

  return shiftTypes?.find((item) => item?.id === id)?.name
}

export const getLastChangedBy = (
  updatedByUser: User | undefined,
  instanceDetail: any
) => {
  return `${updatedByUser?.name?.first} ${
    updatedByUser?.name?.last
  } · ${getDateTimeInZone(instanceDetail?.updatedAt, true)?.format(
    TIME_FIRST_DATE_FORMAT
  )}`
}

export const getWarningsCount = (
  type: PublicationType,
  warnings: CountChangesResponse['warnings'] | undefined
) => {
  if (!warnings) {
    return { warningStatuses: '?', missingAllocations: '?' }
  }

  if (type === 'publish_all') {
    return {
      warningStatuses:
        warnings.warningDraftStatus + warnings.warningChangedStatus,
      missingAllocations:
        warnings.missingDraftAllocations + warnings.missingChangedAllocations,
    }
  }

  if (type === 'publish_changes') {
    return {
      warningStatuses: warnings.warningChangedStatus,
      missingAllocations: warnings.missingChangedAllocations,
    }
  }

  if (type === 'publish_drafts') {
    return {
      warningStatuses:
        warnings.warningDraftStatus + warnings.warningChangedDraftStatus,
      missingAllocations:
        warnings.missingDraftAllocations +
        warnings.missingChangedDraftAllocations,
    }
  }

  return {
    warningStatuses: '?',
    missingAllocations: '?',
  }
}

export const getPublishCount = (
  type: PublicationType,
  hasFilters: boolean,
  countChanges?: CountChangesResponse,
) => {
   if (type === 'publish_all') {
    return hasFilters
    ? countChanges?.filteredUpdates || 0
    : countChanges?.totalUpdates || 0
  }

  if (type === 'publish_changes') {
    return hasFilters
    ? countChanges?.filteredPublishedChanges || 0
    : countChanges?.changesToPublishedEvents || 0
  }

  if (type === 'publish_drafts') {
    return hasFilters
    ? (countChanges?.filteredDrafts || 0)
    : (countChanges?.draftEvents || 0)
  }

  return 0 
}

export const getPublishType = (
  hasDrafts: boolean,
  hasChanges: boolean,
  excludeDrafts: boolean,
  isPublishAll: boolean
): PublicationType => {
  if (excludeDrafts || (hasChanges && !hasDrafts)) {
    return 'publish_changes'
  }

  if (isPublishAll) {
    return 'publish_all'
  }

  if (hasDrafts && !hasChanges) {
    return 'publish_drafts'
  }

  return 'publish_all'
}

export const openGoogleMaps = (address: string) => {
  const encodedAddress = encodeURIComponent(address)
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`
  window.open(googleMapsUrl, '_blank')
}

export const getOverlayTypeByErrorCode = (errorCode?: number) => {
  if (errorCode === 422) {
    return START_OVERLAY_TYPE.NO_PATTERNS
  }

  if (errorCode === 404) {
    return START_OVERLAY_TYPE.NO_ROTA
  }

  return undefined
}
